import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DoorOpen, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { api, errorText } from "@/lib/api";
import { BrandMark } from "@/components/onboarding/BrandMark";
import { useBrandTitle } from "@/lib/useBrandTitle";

export default function Login() {
  const { login, register } = useAuth();
  const [mode] = useState("signin");
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const signup = mode === "signup";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const slug = new URLSearchParams(window.location.search).get("p");
  const [brand, setBrand] = useState(null);
  useEffect(() => { if (slug) api.get(`/portal/${slug}`).then((r) => setBrand(r.data)).catch(() => {}); }, [slug]);
  useBrandTitle(brand);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      signup ? await register(form) : await login(form.email, form.password);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`min-h-screen grid bg-[#07090E] ${slug ? "" : "lg:grid-cols-[1.1fr_1fr]"}`}>
      {!slug && <section className="hidden lg:flex flex-col justify-between p-14 grid-bg border-r border-[#222D3D]">
        <Brand />
        <div className="fade-up max-w-lg">
          <p className="font-mono text-xs text-emerald-400 tracking-[0.3em] uppercase mb-6">Agent inventory</p>
          <h1 className="text-5xl font-semibold leading-[1.05] text-slate-100">
            Every AI agent that crosses your network gets written down.
          </h1>
          <p className="mt-6 text-slate-400 text-base leading-relaxed">
            Every AI agent that crosses your network gets checked and written down: which agent it is, who sent it, where on the network and when. Each entry is chained to the last and anchored to Bitcoin, so the record can't be quietly rewritten.
          </p>
        </div>
        <p className="font-mono text-[11px] text-slate-600">sha256 · hash chain · opentimestamps</p>
      </section>}

      <section className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          {slug ? (brand && <BrandMark brand={brand} />) : <div className="lg:hidden"><Brand /></div>}
          <div>
            <h2 className="text-2xl font-semibold text-slate-100" data-testid="auth-title">{signup ? "Create your account" : "Sign in"}</h2>
            <p className="text-sm text-slate-500 mt-1">{signup ? "Your own private ledger, devices and Bitcoin anchors." : "Access your presence ledger."}</p>
          </div>
          {signup && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input id="name" required value={form.name} onChange={set("name")} data-testid="auth-name-input" className="bg-[#0F141C]" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company <span className="text-slate-600">(optional)</span></Label>
                <Input id="company" value={form.company} onChange={set("company")} data-testid="auth-company-input" className="bg-[#0F141C]" />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="auth-email-input" className="bg-[#0F141C]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={signup ? 8 : undefined} value={form.password} onChange={set("password")} data-testid="auth-password-input" className="bg-[#0F141C]" />
            {signup && <p className="text-xs text-slate-600">At least 8 characters.</p>}
          </div>
          {error && <p className="text-sm text-rose-400" data-testid="auth-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="auth-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : signup ? "Create account" : "Enter"}
          </Button>
          {slug ? <p className="text-sm text-slate-500 text-center">New property? <Link to={`/p/${slug}`} className="text-slate-200 underline underline-offset-4" data-testid="auth-portal-signup-link">Sign up</Link></p> : <div className="text-sm text-slate-500 text-center space-y-1">
            <p>New property? <Link to="/signup" className="text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="auth-property-signup-link">Sign up your property</Link></p>
            <p>Managed service provider? <Link to="/msp/signup" className="text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="auth-msp-signup-link">Apply as an MSP</Link></p>
          </div>}
        </form>
      </section>
    </div>
  );
}

export const Brand = ({ compact = false }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <div className="relative h-9 w-9 shrink-0 grid place-items-center rounded-md bg-[#141A24] border border-[#222D3D]">
      <DoorOpen className="h-5 w-5 text-slate-200" />
      <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />
    </div>
    <span className={`font-display text-lg font-semibold tracking-tight text-slate-100 whitespace-nowrap ${compact ? "hidden md:inline" : ""}`}>Agent<span className="text-emerald-400">Inventory</span>List<span className="text-slate-500 hidden sm:inline">.com</span></span>
  </div>
);
